import React from 'react';
import { format } from 'date-fns';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';
import { Wrench, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuthStore } from '../../stores/authStore';
import type { Message } from '../../services/aiChatApi';

interface AiChatMessageProps {
  message: Message;
}

const markdownComponents = {
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-xl font-bold mt-3 mb-1.5" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-lg font-semibold mt-2.5 mb-1.5" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-base font-semibold mt-2 mb-1" {...props}>{children}</h3>
  ),
  p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="mb-2 last:mb-0 leading-relaxed" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="list-disc list-inside space-y-1 mb-2 ml-1" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="list-decimal list-inside space-y-1 mb-2 ml-1" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="leading-relaxed" {...props}>{children}</li>
  ),
  blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="border-l-4 border-gray-300 dark:border-gray-500 pl-3 my-2 italic text-gray-600 dark:text-gray-300" {...props}>{children}</blockquote>
  ),
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} className="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300 break-all" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
  strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <em className="italic" {...props}>{children}</em>
  ),
  hr: () => <hr className="my-4 border-gray-200 dark:border-gray-600" />,
  pre: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => (
    <pre className="bg-gray-100 dark:bg-gray-700 rounded p-3 overflow-x-auto whitespace-pre-wrap max-w-full mb-2 font-mono text-sm" {...props}>{children}</pre>
  ),
  code: ({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) => {
    const isBlock = /language-/.test(className || '');
    if (isBlock) {
      return <code className={className} {...props}>{children}</code>;
    }
    return <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-sm font-mono" {...props}>{children}</code>;
  },
};

export const AiChatMessage: React.FC<AiChatMessageProps> = ({ message }) => {
  const dateFnsLocale = useDateFnsLocale();
  const isUser = message.role === 'user';
  const isTool = message.role === 'tool';
  const formattedTime = message.createdAt ? format(new Date(message.createdAt), 'HH:mm', { locale: dateFnsLocale }) : '';
  const isAdmin = useAuthStore().user?.role === 'ADMIN';

  if (isTool) {
    if (!isAdmin) {
      return null;
    }
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] md:max-w-[75%]">
          <details className="group">
            <summary className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-pointer text-xs text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 transition-colors select-none list-none">
              <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
              <Wrench className="w-3.5 h-3.5" />
              <span className="font-medium">{message.toolName || 'Tool'}</span>
            </summary>
            <div className="mt-1 px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words font-mono">
                {message.content}
              </p>
            </div>
          </details>
          {formattedTime && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 ml-1">{formattedTime}</p>
          )}
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] md:max-w-[75%]">
          <div className="px-3 py-2 rounded-2xl rounded-br-md bg-indigo-600 dark:bg-indigo-500 text-white">
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          </div>
          {formattedTime && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 mr-1 text-right">{formattedTime}</p>
          )}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] md:max-w-[75%]">
        <div className="px-3 py-2 rounded-2xl rounded-bl-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          <div className="text-sm leading-relaxed break-words">
            <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
          </div>
        </div>
        {formattedTime && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 ml-1">{formattedTime}</p>
        )}
      </div>
    </div>
  );
};

export default AiChatMessage;
